import {
  ApolloLink,
  Observable,
  type ApolloClient,
} from '@material-bank/graphql-client'
import {
  CONSTRUCTOR_POD_IDS,
  CONSTRUCTOR_RESULT_FIELDS,
  DEFAULT_RESULTS_PER_PAGE,
  buildPairsWithRecommendationParams,
  buildSimilarRecommendationParams,
  toPairsWithProduct,
  toSimilarProduct,
  type ConstructorResult,
  type EntitlementFilters,
  type PairsWithProduct,
  type RecommendationParams,
  type SimilarProduct,
  type SimilarProductImage,
} from '@material-bank/mtag-constructor'
import {
  getConstructorBrowserClient,
  preloadConstructorSdk,
} from './browserClient'
import {
  CONSTRUCTOR_USER_CONTEXT_QUERY,
  type ConstructorUserContext,
  type ConstructorUserContextQueryData,
} from './constructorUserContextQuery'

// The BFF's default, so both paths send the same request.
const DEFAULT_LOCALE = 'en-US'
// A request that stalls (a blocker that drops it, a weak mobile connection) would
// otherwise leave the screen loading; timing out hands it to the BFF instead.
const REQUEST_TIMEOUT_MS = 5000

type Variables = Record<string, unknown>

type OperationHandler = (
  variables: Variables,
  userContext: ConstructorUserContext,
) => Promise<Record<string, unknown>>

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

function getLocale(variables: Variables): string {
  return stringOrUndefined(variables.locale) ?? DEFAULT_LOCALE
}

function getResultsPerPage(variables: Variables, fallback: number): number {
  return typeof variables.resultsPerPage === 'number'
    ? variables.resultsPerPage
    : fallback
}

function getEntitlementFilters(
  userContext: ConstructorUserContext,
): EntitlementFilters {
  return {
    ruleGroupIds: userContext.ruleGroupIds ?? undefined,
    excludedRuleGroupIds: userContext.excludedRuleGroupIds ?? undefined,
  }
}

async function getRecommendationResults(
  podId: string,
  params: RecommendationParams,
  locale: string,
  userContext: ConstructorUserContext,
): Promise<ConstructorResult[]> {
  const client = await getConstructorBrowserClient(locale, userContext)
  const response = await client.recommendations.getRecommendations(
    podId,
    { ...params, fmtOptions: { fields: CONSTRUCTOR_RESULT_FIELDS } },
    { timeout: REQUEST_TIMEOUT_MS },
  )

  return (response?.response?.results ?? []) as ConstructorResult[]
}

// Apollo reports a field that is undefined rather than null as missing, so the
// image objects are rebuilt with exactly the selected fields.
function toImage(image: SimilarProductImage | null) {
  if (!image) {
    return null
  }

  return {
    __typename: 'SimilarProductImage',
    url: image.url,
    label: image.label ?? null,
  }
}

function toSimilarProductData(product: SimilarProduct) {
  return {
    __typename: 'SimilarProduct',
    ...product,
    hoverImage: toImage(product.hoverImage),
    mainImage: toImage(product.mainImage),
  }
}

function toPairsWithProductData(product: PairsWithProduct) {
  return { __typename: 'PairsWithProduct', ...product }
}

const handlers: Record<string, OperationHandler> = {
  async SimilarProducts(variables, userContext) {
    // Similar is for signed-in members only; the BFF fallback refuses guests too.
    if (!userContext.isMember) {
      return {
        similarProducts: {
          __typename: 'SimilarProductsResult',
          products: [],
          totalCount: 0,
        },
      }
    }

    const locale = getLocale(variables)
    const categoryGroupIds = Array.isArray(variables.categoryGroupIds)
      ? (variables.categoryGroupIds as string[])
      : undefined
    const results = await getRecommendationResults(
      CONSTRUCTOR_POD_IDS.findSimilar,
      buildSimilarRecommendationParams({
        sku: String(variables.sku),
        variationSku: stringOrUndefined(variables.variationSku),
        categoryGroupIds,
        resultsPerPage: getResultsPerPage(
          variables,
          DEFAULT_RESULTS_PER_PAGE.findSimilar,
        ),
        ...getEntitlementFilters(userContext),
      }),
      locale,
      userContext,
    )
    const products = results.map(toSimilarProduct)

    return {
      similarProducts: {
        __typename: 'SimilarProductsResult',
        products: products.map(toSimilarProductData),
        totalCount: products.length,
      },
    }
  },

  async PairsWithProducts(variables, userContext) {
    const locale = getLocale(variables)
    const results = await getRecommendationResults(
      CONSTRUCTOR_POD_IDS.pairsWith,
      buildPairsWithRecommendationParams({
        sku: String(variables.sku),
        resultsPerPage: getResultsPerPage(
          variables,
          DEFAULT_RESULTS_PER_PAGE.pairsWith,
        ),
        ...getEntitlementFilters(userContext),
      }),
      locale,
      userContext,
    )
    const products = results.map(toPairsWithProduct)

    return {
      pairsWithProducts: {
        __typename: 'PairsWithProductsResult',
        products: products.map(toPairsWithProductData),
        totalCount: products.length,
      },
    }
  },
}

class DegradedUserContextError extends Error {
  constructor() {
    super('Constructor user context is degraded')
    this.name = 'DegradedUserContextError'
  }
}

// Read through the cache so it's fetched once per identity: AuthApolloBridge's
// resetStore on sign-in or sign-out clears it along with the results it filtered.
// A degraded context (the BFF's public-only fallback during an entitlement outage)
// is dropped instead, so it isn't kept for the session: the request goes to the
// BFF, which retries the lookup, and the next one reads the context afresh.
async function getUserContext(
  client: ApolloClient,
): Promise<ConstructorUserContext> {
  const { data } = await client.query<ConstructorUserContextQueryData>({
    query: CONSTRUCTOR_USER_CONTEXT_QUERY,
    fetchPolicy: 'cache-first',
  })

  if (!data) {
    throw new Error('Constructor user context unavailable')
  }

  if (data.constructorUserContext.isDegraded) {
    client.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'constructorUserContext',
    })
    client.cache.gc()
    throw new DegradedUserContextError()
  }

  return data.constructorUserContext
}

// Why the BFF served a request: the kill switch, or the stage of the browser
// path that failed.
type BffReason =
  | 'killSwitch'
  | 'userContext'
  | 'degradedContext'
  | 'constructor'

// One New Relic event per request, so the share that still reaches Constructor
// from the BFF (ad blockers, timeouts, the kill switch) can be measured. A no-op
// when the Browser agent isn't on the page.
function recordOutcome(
  operationName: string,
  outcome:
    | { source: 'browser'; startedAt: number }
    | { source: 'bff'; reason: 'killSwitch' }
    | { source: 'bff'; reason: BffReason; startedAt: number; error: unknown },
) {
  if (typeof window === 'undefined') {
    return
  }

  const attributes: Record<string, string | number> = {
    operation: operationName,
    source: outcome.source,
  }

  if ('startedAt' in outcome) {
    attributes.durationMs = Math.round(performance.now() - outcome.startedAt)
  }

  if (outcome.source === 'bff') {
    attributes.reason = outcome.reason
  }

  if ('error' in outcome) {
    const { error } = outcome
    attributes.error = (
      error instanceof Error ? `${error.name}: ${error.message}` : String(error)
    ).slice(0, 200)
  }

  window.newrelic?.recordCustomEvent?.('ConstructorRecommendations', attributes)
}

function isBrowserSourceEnabled(): boolean {
  return (
    typeof window !== 'undefined' &&
    process.env.NEXT_PUBLIC_CONSTRUCTOR_RECOMMENDATIONS_SOURCE !== 'bff'
  )
}

// Find Similar and Pairs With need the SDK and the user context before they can
// call Constructor. The PDP prefetch only starts after hydration, which takes
// seconds on a slow phone; starting both when the client is created takes them
// off the path of an early tap.
export function prefetchConstructorRecommendations(client: ApolloClient): void {
  if (!isBrowserSourceEnabled()) {
    return
  }

  preloadConstructorSdk()
  getUserContext(client).catch(() => {
    // The link reads it again when it needs it, and falls back to the BFF then.
  })
}

// Answers the Find Similar and Pairs With queries by calling Constructor from the
// browser, so Constructor sees the shopper's own user agent, IP and ids rather
// than the BFF's. Any failure — the user context, the SDK import, an ad blocker,
// Constructor itself — hands the operation on to the BFF, which serves the same
// result. A request is never sent without the caller's entitlement filters: if
// they can't be read, the BFF applies them.
export function createConstructorRecommendationsLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const handler = handlers[operation.operationName ?? '']

    if (!handler) {
      return forward(operation)
    }

    if (!isBrowserSourceEnabled()) {
      recordOutcome(operation.operationName ?? '', {
        source: 'bff',
        reason: 'killSwitch',
      })

      return forward(operation)
    }

    return new Observable<ApolloLink.Result>((observer) => {
      let isClosed = false
      let fallback: { unsubscribe: () => void } | null = null
      let stage: BffReason = 'userContext'
      const operationName = operation.operationName ?? ''
      const startedAt = performance.now()

      preloadConstructorSdk()
      getUserContext(operation.client)
        .then((userContext) => {
          stage = 'constructor'

          return handler(operation.variables, userContext)
        })
        .then((data) => {
          if (isClosed) {
            return
          }

          recordOutcome(operationName, { source: 'browser', startedAt })
          observer.next({ data })
          observer.complete()
        })
        .catch((error: unknown) => {
          if (isClosed) {
            return
          }

          recordOutcome(operationName, {
            source: 'bff',
            reason:
              error instanceof DegradedUserContextError
                ? 'degradedContext'
                : stage,
            startedAt,
            error,
          })
          console.warn(
            `Constructor ${operation.operationName} failed in the browser, using the BFF:`,
            error instanceof Error ? error.message : error,
          )
          fallback = forward(operation).subscribe(observer)
        })

      return () => {
        isClosed = true
        fallback?.unsubscribe()
      }
    })
  })
}
